import React, { useState, useEffect } from 'react';
import { X, Building2, DollarSign, Percent, CheckCircle2 } from 'lucide-react';
import { propertiesAPI } from '../services/api';
import { formatCurrency } from '../utils/formatters';

function formatPKR(amount) {
  const num = Number(amount) || 0;
  if (num >= 10000000) return `PKR ${(num / 10000000).toFixed(1).replace(/\.0$/, '')} Crore`;
  if (num >= 100000) return `PKR ${(num / 100000).toFixed(1).replace(/\.0$/, '')} Lac`;
  if (num >= 1000) return `PKR ${(num / 1000).toFixed(1).replace(/\.0$/, '')} K`;
  return formatCurrency(num);
}

function formatSouthAsian(amount) {
  const num = Number(amount) || 0;
  if (num === 0) return '';
  const str = num.toLocaleString('en-IN');
  return `PKR ${str}`;
}

const DealWonModal = ({ isOpen, onClose, onConfirm, leadName }) => {
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [listedPrice, setListedPrice] = useState(0);
  const [actualSellingAmount, setActualSellingAmount] = useState('');
  const [commissionAmount, setCommissionAmount] = useState('');
  const [commissionPercent, setCommissionPercent] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchProperties();
      // Reset form
      setSelectedPropertyId('');
      setListedPrice(0);
      setActualSellingAmount('');
      setCommissionAmount('');
      setCommissionPercent('');
    }
  }, [isOpen]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const res = await propertiesAPI.getAll({ status: 'available,reserved' });
      const uniqueProps = res.data?.properties || res.data || [];
      
      setProperties(uniqueProps);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePropertyChange = (e) => {
    const propId = e.target.value;
    setSelectedPropertyId(propId);
    if (!propId) {
      setListedPrice(0);
      setActualSellingAmount('');
      return;
    }
    const prop = properties.find(p => p.id.toString() === propId);
    if (prop) {
      setListedPrice(prop.price || 0);
      setActualSellingAmount(prop.price || '');
      calculateCommission(prop.price, commissionPercent, null);
    }
  };

  const calculateCommission = (sellingAmt, percent, amount) => {
    const sale = parseFloat(sellingAmt);
    if (isNaN(sale) || sale === 0) {
      if (percent !== null) setCommissionAmount('');
      if (amount !== null) setCommissionPercent('');
      return;
    }

    if (percent !== null) {
      const p = parseFloat(percent);
      if (!isNaN(p)) {
        setCommissionAmount((sale * (p / 100)).toFixed(2));
      } else {
        setCommissionAmount('');
      }
    } else if (amount !== null) {
      const a = parseFloat(amount);
      if (!isNaN(a)) {
        setCommissionPercent(((a / sale) * 100).toFixed(2));
      } else {
        setCommissionPercent('');
      }
    }
  };

  const handleSellingAmountChange = (e) => {
    const val = e.target.value;
    setActualSellingAmount(val);
    calculateCommission(val, commissionPercent, null);
  };

  const handlePercentChange = (e) => {
    const val = e.target.value;
    setCommissionPercent(val);
    calculateCommission(actualSellingAmount, val, null);
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    setCommissionAmount(val);
    calculateCommission(actualSellingAmount, null, val);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedPropertyId) {
      alert("Please select a property");
      return;
    }
    onConfirm({
      sold_property_id: selectedPropertyId,
      deal_value: Number(actualSellingAmount),
      commission: Number(commissionAmount)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
              <CheckCircle2 size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>Deal Won!</h3>
              <p className="text-sm text-gray-500">Confirm deal details for <span className="font-medium text-gray-700">{leadName}</span></p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
          
          {/* Property Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
              <Building2 size={16} className="text-gray-400" />
              What was sold?
            </label>
            <select
              value={selectedPropertyId}
              onChange={handlePropertyChange}
              required
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-gray-50"
            >
              <option value="" disabled>Select from available inventory...</option>
              {loading ? (
                <option disabled>Loading inventory...</option>
              ) : (
                properties.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.title} (PKR {Number(p.price).toLocaleString()})
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-4">
            {/* Selling Amount */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <DollarSign size={16} className="text-gray-400" />
                  Actual Selling Amount (PKR)
                </span>
                {listedPrice > 0 && (
                  <span className="text-xs text-gray-500 font-normal">Listed: {listedPrice.toLocaleString()}</span>
                )}
              </label>
              <input
                type="number"
                required
                value={actualSellingAmount}
                onChange={handleSellingAmountChange}
                placeholder="e.g. 15000000"
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              {actualSellingAmount && Number(actualSellingAmount) > 0 && (
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>{formatSouthAsian(actualSellingAmount)}</span>
                  <span style={{ background: '#F3F4F6', padding: '1px 8px', borderRadius: '4px', fontWeight: 500, color: '#374151' }}>{formatPKR(actualSellingAmount)}</span>
                </div>
              )}
            </div>

            {/* Commission */}
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <Percent size={16} className="text-gray-400" />
                  Commission %
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={commissionPercent}
                  onChange={handlePercentChange}
                  placeholder="e.g. 2.0"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <DollarSign size={16} className="text-gray-400" />
                  Commission Amount
                </label>
                <input
                  type="number"
                  required
                  value={commissionAmount}
                  onChange={handleAmountChange}
                  placeholder="e.g. 300000"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors shadow-sm"
            >
              Confirm & Save Deal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DealWonModal;
